import {
    DialogTitle,
    DialogContent,
    Grid,
    Stack,
    Alert,
    DialogActions,
    Box,
    Typography,
    Tooltip,
    IconButton,
    Divider
} from '@mui/material'
import { useFieldArray, useForm } from 'react-hook-form'
import { z } from 'zod'
import { EtapaPostFields, EtapaPermisoPostFields } from '../api/schemas'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import { AsyncSelect, Button, ControlledTextField } from 'src/components/forms'
import { zodResolver } from '@hookform/resolvers/zod'
import { HttpCacheWorkflow } from 'src/http/workflow/httpCacheWorkflow'
import { HttpEtapasABM } from '../api/httpEtapasABM'
import Select from 'src/components/forms/Select'
import { Etapa } from '../types'
import { HttpCacheGrupo } from 'src/http/cache/httpCacheGrupo'
import { ControlledAutocomplete } from 'src/components/forms/ControlledAutocomplete'
import { useEffect, useState } from 'react'
import { EntityWithIdAndDescription } from 'src/lib/itaptec/http_client/src'

const schemaFormPostEtapa = z.object({
    codSistemaPrincipal: z.number(),
    [EtapaPostFields.CodSistema]: z.number(),
    [EtapaPostFields.NombreEtapa]: z.string(),
    [EtapaPostFields.DiasDuracion]: z.coerce.number(),
    [EtapaPostFields.CostoBase]: z.coerce.number(),
    [EtapaPostFields.CostoDiario]: z.coerce.number(),
    [EtapaPostFields.TipoEtapa]: z.string(),
    [EtapaPostFields.IdEtapaVirtual]: z.coerce.number(),
    [EtapaPostFields.Orden]: z.coerce.number(),
    [EtapaPostFields.Permisos]: z
        .array(
            z.object({
                // Esto medio que es un hack, porque al hacer "append" sí o sí hay que pasarle valores válidos
                // (a diferencia de defaultValues). La discusión al respecto:
                // https://github.com/orgs/react-hook-form/discussions/10211
                [EtapaPermisoPostFields.IdGrupo]: z.number().positive({ message: 'Debe incluir un grupo.' }),
                [EtapaPermisoPostFields.PermisoTipo]: z.number().positive({ message: 'Debe incluir un permiso.' })
            })
        )
        .nullable()
})

type SchemaFormPostEtapa = z.infer<typeof schemaFormPostEtapa>

type DialogEtapaABMProps = {
    closeDialog: () => void
    etapa?: Etapa
}

export const DialogEtapaABM = ({ closeDialog, etapa }: DialogEtapaABMProps) => {
    const [gruposPorSistema, setGruposPorSistema] = useState<EntityWithIdAndDescription[]>()

    const { control, handleSubmit, watch } = useForm<SchemaFormPostEtapa>({
        resolver: zodResolver(schemaFormPostEtapa),
        values: etapa
    })

    const codSistemaSeleccionado = watch(EtapaPostFields.CodSistema)

    useEffect(() => {
        HttpCacheGrupo.getGruposPorSistema(codSistemaSeleccionado).then(setGruposPorSistema)
    }, [codSistemaSeleccionado])

    const { fields, append, remove } = useFieldArray({
        name: EtapaPostFields.Permisos,
        control
    })

    const submitEtapa = (data: SchemaFormPostEtapa) => {
        return etapa ? HttpEtapasABM.putEtapa(etapa.id, data) : HttpEtapasABM.postEtapa(data)
    }

    const onSubmitWrapper = () => {
        return new Promise<void>((resolve, reject) => {
            handleSubmit(
                formData => {
                    submitEtapa(formData)
                        .then(() => {
                            setTimeout(closeDialog, 500)
                            resolve()
                        })
                        .catch(reject)
                },
                validationErrors => {
                    reject(validationErrors)
                }
            )()
        })
    }

    return (
        <>
            <DialogTitle>Crear nueva etapa</DialogTitle>
            <DialogContent>
                <Grid container spacing={4}>
                    <Grid item md={6} spacing={2}>
                        <Stack spacing={2}>
                            <Typography>General</Typography>
                            <Divider />
                            <Box>
                                <Grid container spacing={4}>
                                    <Grid item xs={8}>
                                        <ControlledTextField
                                            control={control}
                                            name={EtapaPostFields.NombreEtapa}
                                            label='Nombre'
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={4}>
                                        <ControlledTextField
                                            control={control}
                                            name={EtapaPostFields.TipoEtapa}
                                            label='Tipo etapa'
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Select
                                            control={control}
                                            name='codSistemaPrincipal'
                                            label='Sistema Principal'
                                            loadOptions={HttpCacheWorkflow.getSistemasPrincipales}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <AsyncSelect
                                            control={control}
                                            name={EtapaPostFields.CodSistema}
                                            label='Sistema'
                                            loadOptions={() =>
                                                HttpCacheWorkflow.getSistemasPorSistemaPrincipal(
                                                    watch('codSistemaPrincipal')
                                                )
                                            }
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <ControlledTextField
                                            control={control}
                                            name={EtapaPostFields.DiasDuracion}
                                            label='Días duración'
                                            type='number'
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <ControlledTextField
                                            control={control}
                                            name={EtapaPostFields.CostoBase}
                                            label='Costo base'
                                            type='number'
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <ControlledTextField
                                            control={control}
                                            name={EtapaPostFields.CostoDiario}
                                            label='Costo diario'
                                            type='number'
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <ControlledTextField
                                            control={control}
                                            name={EtapaPostFields.IdEtapaVirtual}
                                            label='Id etapa virtual'
                                            type='number'
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <ControlledTextField
                                            control={control}
                                            name={EtapaPostFields.Orden}
                                            label='Orden'
                                            type='number'
                                            fullWidth
                                        />
                                    </Grid>
                                </Grid>
                            </Box>
                        </Stack>
                    </Grid>
                    <Grid item md={6} spacing={2}>
                        <Stack spacing={2}>
                            <Typography>Permisos</Typography>
                            <Divider />
                            <Stack spacing={2}>
                                {!fields.length ? (
                                    <Alert severity='info'>No se adjudicaron permisos.</Alert>
                                ) : (
                                    fields.map((field, index) => {
                                        return (
                                            <Box key={field.id}>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={7}>
                                                        <AsyncSelect
                                                            loadOptions={() => {
                                                                return new Promise<EntityWithIdAndDescription[]>(
                                                                    resolve => resolve(gruposPorSistema || [])
                                                                )
                                                            }}
                                                            control={control}
                                                            label='Grupo'
                                                            name={`${EtapaPostFields.Permisos}.${index}.${EtapaPermisoPostFields.IdGrupo}`}
                                                            fullWidth
                                                        />
                                                    </Grid>
                                                    <Grid item xs={4}>
                                                        <Select
                                                            loadOptions={[
                                                                {
                                                                    id: 1,
                                                                    descripcion: 'Invitado'
                                                                },
                                                                {
                                                                    id: 2,
                                                                    descripcion: 'Miembro'
                                                                },
                                                                {
                                                                    id: 3,
                                                                    descripcion: 'Responsable'
                                                                }
                                                            ]}
                                                            control={control}
                                                            label='Permiso'
                                                            name={`${EtapaPostFields.Permisos}.${index}.${EtapaPermisoPostFields.PermisoTipo}`}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={1}>
                                                        <Tooltip title='Quitar permiso'>
                                                            <IconButton onClick={() => remove(index)}>
                                                                <DeleteIcon color='error' />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Grid>
                                                </Grid>
                                            </Box>
                                        )
                                    })
                                )}
                            </Stack>
                            <Box display='flex' justifyContent='center'>
                                <Tooltip title='Agregar permiso'>
                                    <IconButton
                                        onClick={() =>
                                            append({
                                                [EtapaPermisoPostFields.IdGrupo]: 0,
                                                [EtapaPermisoPostFields.PermisoTipo]: 0
                                            })
                                        }
                                    >
                                        <AddIcon color='primary' />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Stack>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button variant='text' color='primary' onClick={closeDialog}>
                    Cancelar
                </Button>
                <Button variant='contained' color='primary' promise={onSubmitWrapper}>
                    Guardar
                </Button>
            </DialogActions>
        </>
    )
}
